import React, { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Backdrop,
  CircularProgress,
  CssBaseline,
  Snackbar,
  ThemeProvider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SignupStep2 from './signupStep2';
import SignupStep1 from './signupStep1';
import SignupStep3 from './signupStep3';
import SignupStep4 from './signupStep4';
import SignupStep5 from './signupStep5';
import SignupStep6 from './signupStep6';
import SignupFinancialSupport from './signupFinancialSupport';
import SignupShell from './signupShell';
import signupTheme from './signupTheme';
import { register } from '../../services/authServices';
import {
  buildScoreCustomerSignupFields,
  isScoreCustomerPersona,
  readSignupUrlParams,
} from './scoreCustomerSignup';

const initialFormData = {
  registrationType: 'individual',
  userTypeId: '',
  firstName: '',
  lastName: '',
  email: '',
  serviceProviderType: '',
  // The selected persona's name (userTypes[].typeName), kept so the payload can tell personas apart.
  personaName: '',
  // eK Score Customer only: organisation type and, for an individual, the organisation name.
  scoreCustomerType: '',
  scoreCustomerOrganizationName: '',
  countryCode: '',
  phoneNumber: '',
  termsAndPolicies: false,
  otpVerified: false,
  organisationName: '',
  administrativeRegion: '',
  addressTemplate: null,
  addressLine1: '',
  addressLine2: '',
  level1: '',
  level2: '',
  level3: '',
  level4: '',
  pincode: '',
  initiativeEcosystemToJoin: [],
  preferredEcosystemTypes: [],
  subscriptionPlanId: '',
  isCustomPlanRequest: false,
  subscriptionPlanMonthlyPrice: 0,
  subscriptionPlanYearlyPrice: 0,
  subscriptionPlanSetupFee: 0,
  subscriptionPlanYearlySetupFee: 0,
  preferredPaymentMethodId: '',
  preferredPaymentMethodType: '',
  paymentCardNumber: '',
  paymentCardExpiry: '',
  paymentCardCvc: '',
  promoCode: '',
  promoDiscountAmount: 0,
  subscriptionBillingCycle: 'monthly',
  subscriptionPlanName: '',
  subscriptionPlanCurrency: '$',
  financialSupportRequested: false,
  financialSupportType: '',
  financialSupportPlanId: '',
  financialSupportPlanName: '',
  financialSupportProviderName: '',
  financialSupportRepaymentSchedule: '',
  financialSupportInterestRate: '',
  financialSupportAmount: '',
  financialSupportConditions: [],
  financialSupportCoveragePercentage: 0,
  financialSupportCoverageAmount: 0,
  financialSupportUserPaymentAmount: 0,
  gracePeriodStartDate: null,
  gracePeriodEndDate: null,
  gracePeriodMonths: 6,
  preferredPaymentMethodName: '',
  preferredPaymentProvider: '',
  reviewConfirmed: false,
  ecosystemIntegratorId: null,
  clientSecret: null,
  paymentIntentId: null,
};


const SignUp = () => {
  const [signupStep, setSignupStep] = useState(1);
  const [showFinancialSupportApplication, setShowFinancialSupportApplication] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  // Closing keeps the message and severity, so the fade-out shows this alert, not an empty
  // blue 'info' one.
  const closeFeedback = () => setFeedback((current) => current && { ...current, open: false });
  const [formData, setFormData] = useState(initialFormData);
  const navigate = useNavigate();
  // ?persona= preselects a persona, ?returnUrl= is offered on the completion screen.
  const [urlParams] = useState(() => readSignupUrlParams());
  const currentStepRef = useRef(1);
  const currentFinancialSupportRef = useRef(false);
  const isHandlingPopState = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    currentStepRef.current = signupStep;
    currentFinancialSupportRef.current = showFinancialSupportApplication;
  }, [signupStep, showFinancialSupportApplication]);

  // Set up browser back button handler
  useEffect(() => {
    const handlePopState = () => {
      isHandlingPopState.current = true;

      if (currentStepRef.current === 4 && currentFinancialSupportRef.current) {
        setShowFinancialSupportApplication(false);
      } else if (currentStepRef.current > 1) {
        setSignupStep((currentStep) => currentStep - 1);
      }
    };

    // Initialize history state
    window.history.replaceState({ step: 1 }, '', window.location.href);

    // Add popstate listener
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Push new state when step changes
  useEffect(() => {
    if (!isHandlingPopState.current) {
      window.history.pushState({ step: signupStep, financialSupport: showFinancialSupportApplication }, '', window.location.href);
    } else {
      isHandlingPopState.current = false;
    }
  }, [signupStep, showFinancialSupportApplication]);

  const handleContinue = () => {
    if (signupStep < 6) {
      setSignupStep((currentStep) => currentStep + 1);
    }
  };

  const handleBack = () => {
    if (signupStep === 4 && showFinancialSupportApplication) {
      setShowFinancialSupportApplication(false);
      return;
    }

    if (signupStep > 1) {
      setSignupStep((currentStep) => currentStep - 1);
    }
  };

  const handleOpenFinancialSupport = () => {
    setShowFinancialSupportApplication(true);
  };

  const handleCloseFinancialSupport = () => {
    setShowFinancialSupportApplication(false);
  };

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData((currentData) => ({
      ...currentData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleComplete = async (completionDetails = {}) => {
    console.log('handleComplete called with:', completionDetails);
    setLoading(true);

    try {
      const trimmedFirstName = (formData.firstName || '').trim();
      const trimmedLastName = (formData.lastName || '').trim();
      const trimmedOrganisationName = (formData.organisationName || '').trim();
      const trimmedAddressLine1 = (formData.addressLine1 || '').trim();
      const trimmedAddressLine2 = (formData.addressLine2 || '').trim();

      // Build address levels map from form data
      const addressLevels = {};
      if (formData.addressTemplate?.levels) {
        formData.addressTemplate.levels.forEach(level => {
          const fieldName = `level${level.level}`;
          if (formData[fieldName]) {
            addressLevels[level.level] = formData[fieldName];
          }
        });
      }

      const organisationDetail = {
        administrativeRegion: formData.administrativeRegion,
        addressLine1: trimmedAddressLine1,
        addressLevels: addressLevels,
        pincode: formData.pincode,
        selectedIntegrators: formData.selectedIntegrators,
        initiativeEcosystemToJoin: formData.initiativeEcosystemToJoin,
        preferredEcosystemTypes: formData.preferredEcosystemTypes,
      };

      if (trimmedAddressLine2) {
        organisationDetail.addressLine2 = trimmedAddressLine2;
      }

      if (formData.registrationType === 'organization' && trimmedOrganisationName) {
        organisationDetail.organisationName = trimmedOrganisationName;
      }

      if (formData.serviceProviderType) {
        organisationDetail.serviceProviderType = formData.serviceProviderType;
      }

      if (isScoreCustomerPersona(formData.personaName)) {
        Object.assign(organisationDetail, buildScoreCustomerSignupFields(formData));
      }

      // For Razorpay payments in India, use the orderId as razorpayOrderId
      // paymentId is null until payment is created by backend after registration
      const subscription = {
        subscriptionPlanId: formData.subscriptionPlanId,
        preferredPaymentMethodId: formData.preferredPaymentMethodId || null,
        preferredPaymentMethodType: formData.preferredPaymentMethodType,
        paymentId: null,
        razorpayOrderId: completionDetails?.orderId || null,
        subscriptionBillingCycle: formData.subscriptionBillingCycle || 'monthly',
        promoCode: formData.promoCode || null,
        promoDiscountAmount: formData.promoDiscountAmount || null,
        financialSupportRequested: formData.financialSupportRequested ? true : null,
        financialSupportType: formData.financialSupportType || null,
        financialSupportPlanId: formData.financialSupportPlanId || null,
        financialSupportAmount: formData.financialSupportAmount || null,
      };
      console.log('📊 Subscription object with razorpayOrderId:', subscription.razorpayOrderId);

      const payload = {
        user: {
          userTypeId: formData.userTypeId,
          registrationType: formData.registrationType,
          firstName:
            formData.registrationType === 'individual' ? trimmedFirstName : '',
          lastName:
            formData.registrationType === 'individual' ? trimmedLastName : '',
          email: formData.email,
          countryCode: formData.countryCode,
          phoneNumber: formData.phoneNumber,
          termsAndPolicies: formData.termsAndPolicies,
          otpVerified: formData.otpVerified,
        },
        organisationDetail,
        subscription,
      };

      const response = await register(payload);

      if (response.statusCode === 200 || response.statusCode === 201) {
        // EK-02: REQUEST_SUBMITTED = the registration waits for approval (every DARE does).
        navigate('/complete', {
          state: {
            ...completionDetails,
            approvalPending: response.resultCode === 'REQUEST_SUBMITTED',
            returnUrl: urlParams.returnUrl,
          },
        });
        return;
      }

      setFeedback({
        severity: 'error',
        message: response.message || 'We could not complete the registration.',
        open: true,
      });
    } catch (error) {
      setFeedback({
        severity: 'error',
        message: 'Something went wrong while creating your account.',
        open: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={signupTheme}>
      <CssBaseline />
      <SignupShell onNavigateLogin={() => navigate('/login')}>
        {signupStep === 1 && (
          <SignupStep1
            handleContinue={handleContinue}
            handleChange={handleChange}
            formData={formData}
            preselectPersona={urlParams.persona}
          />
        )}
        {signupStep === 2 && (
          <SignupStep2
            handleContinue={handleContinue}
            handleBack={handleBack}
            handleChange={handleChange}
            formData={formData}
          />
        )}
        {signupStep === 3 && (
          <SignupStep3
            handleContinue={handleContinue}
            handleBack={handleBack}
            handleChange={handleChange}
            formData={formData}
          />
        )}
        {signupStep === 4 && !showFinancialSupportApplication && (
          <SignupStep4
            handleContinue={handleContinue}
            handleBack={handleBack}
            handleChange={handleChange}
            handleOpenFinancialSupport={handleOpenFinancialSupport}
            formData={formData}
          />
        )}
        {signupStep === 4 && showFinancialSupportApplication && (() => {
          const billingCycle = formData.subscriptionBillingCycle || 'monthly';
          const monthlyPrice = formData.subscriptionPlanMonthlyPrice || 0;
          const yearlyPrice = formData.subscriptionPlanYearlyPrice || 0;
          const setupFee = formData.subscriptionPlanSetupFee || 0;
          const yearlySetupFee = formData.subscriptionPlanYearlySetupFee || 0;

          // For monthly billing, calculate 12 months of subscription; for yearly, use yearly price
          const price = billingCycle === 'yearly' ? yearlyPrice : monthlyPrice * 12;
          const fee = billingCycle === 'yearly' ? yearlySetupFee : setupFee;
          const subscriptionPlanTotal = price + fee;

          return (
            <SignupFinancialSupport
              handleBack={handleCloseFinancialSupport}
              handleChange={handleChange}
              handleApply={handleCloseFinancialSupport}
              formData={formData}
              selectedPlanFinancialSupportDetails={formData.selectedPlanFinancialSupportDetails}
              selectedBillingCycle={formData.subscriptionBillingCycle}
              subscriptionPlanTotal={subscriptionPlanTotal}
            />
          );
        })()}
        {signupStep === 5 && (
          <SignupStep5
            handleContinue={handleContinue}
            handleBack={handleBack}
            handleChange={handleChange}
            formData={formData}
          />
        )}
        {signupStep === 6 && (
          <SignupStep6
            handleBack={handleBack}
            handleChange={handleChange}
            handleComplete={handleComplete}
            formData={formData}
          />
        )}
      </SignupShell>

      <Backdrop
        open={loading}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(28, 27, 31, 0.24)',
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={Boolean(feedback?.open)}
        autoHideDuration={4000}
        onClose={closeFeedback}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={closeFeedback}
          severity={feedback?.severity || 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {feedback?.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default SignUp;
